import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import Logo from "@/components/Logo";
import { CONSENT_EVENT_OPEN } from "@/components/CookieBanner";

export default function Footer() {
  const { t, lang } = useI18n();
  const year = new Date().getFullYear();
  const base = `/${lang}`;

  const openCookiePrefs = () => {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT_OPEN));
  };

  return (
    <footer className="section-dark relative overflow-hidden" data-testid="site-footer">
      <div className="grain absolute inset-0" aria-hidden="true" />
      <div className="container-x relative py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Logo tone="paper" />
            <p className="mt-6 text-[#FAF9F6]/70 max-w-md leading-relaxed">
              {t("footer.tagline")}
            </p>
            <p className="mt-8 font-serif-display text-3xl leading-tight">
              <a href="mailto:hola@ronavirona.com" className="link-underline">
                {t("contact.email_link")}
              </a>
            </p>
            <p className="mt-2 text-[#FAF9F6]/60 text-sm">
              <a href="tel:+541152194820" className="link-underline">
                {t("contact.phone_link")}
              </a>
            </p>
          </div>

          <div className="lg:col-span-2">
            <div className="eyebrow text-[#FAF9F6]/50 mb-4">{t("footer.explore")}</div>
            <ul className="space-y-3 text-sm">
              <li><a href={`${base}#opportunities`} className="link-underline" data-testid="footer-link-opportunities">{t("nav.opportunities")}</a></li>
              <li><a href={`${base}#how`} className="link-underline" data-testid="footer-link-how">{t("nav.how_it_works")}</a></li>
              <li><a href={`${base}#trust`} className="link-underline" data-testid="footer-link-trust">{t("nav.trust")}</a></li>
              <li><a href={`${base}#contact`} className="link-underline" data-testid="footer-link-contact">{t("nav.contact")}</a></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="eyebrow text-[#FAF9F6]/50 mb-4">{t("footer.legal")}</div>
            <ul className="space-y-3 text-sm">
              <li><Link to={`${base}/privacy_policy`} className="link-underline" data-testid="footer-link-privacy">{t("privacy_policy.title")}</Link></li>
              <li><Link to={`${base}/cookie_policy`} className="link-underline" data-testid="footer-link-cookies">{t("cookie_policy.title")}</Link></li>
              <li><Link to={`${base}/terms_and_conditions`} className="link-underline" data-testid="footer-link-terms">{t("terms.title")}</Link></li>
              <li>
                <button type="button" onClick={openCookiePrefs} className="link-underline" data-testid="footer-open-cookie-prefs">
                  {t("cookie.manage")}
                </button>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="eyebrow text-[#FAF9F6]/50 mb-4">{t("footer.contact")}</div>
            <address className="not-italic text-[#FAF9F6]/70 text-sm leading-relaxed">
              {t("footer.company_name")}<br />
              {t("footer.address")}<br />
              {t("footer.cuit")}<br />
              {t("footer.iibb")}<br />
              {t("footer.iva")}
            </address>
          </div>
        </div>

        <div className="rule my-14 opacity-30" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 text-[#FAF9F6]/55 text-xs leading-relaxed">
          <p className="lg:col-span-8">
            {t("footer.registration")} {t("footer.license")} {t("footer.jurisdiction")}
          </p>
          <p className="lg:col-span-4">
            {t("footer.disclaimer")}
          </p>
        </div>

        <div className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-[#FAF9F6]/55 text-xs font-mono-mark tracking-widest uppercase">
            {t("footer.copyright").replace("{year}", year)}
          </p>
          <button
            type="button"
            onClick={() => window.__lenis ? window.__lenis.scrollTo(0, { duration: 1.4 }) : window.scrollTo({ top: 0, behavior: "smooth" })}
            className="text-[#FAF9F6]/70 text-xs font-mono-mark tracking-[0.2em] uppercase hover:text-[#D97706]"
            data-testid="footer-back-to-top"
          >
            {t("footer.back_to_top")} ↑
          </button>
        </div>
      </div>
    </footer>
  );
}
